"use client";

import { createContext, useCallback, useContext, useEffect } from "react";
import { usePathname } from "next/navigation";
import isEqual from "lodash.isequal";
import isEmpty from "lodash.isempty";
import isNull from "lodash.isnull";
import omit from "lodash.omit";
import omitBy from "lodash.omitby";
import { useCurrentAccountId } from "@zapier/identity";
import { SetDefaultViewOptions, useEmitSetDefaultViewEvent } from "@zapier/shared";
import { useNotification, DEFAULT_VIEW_SETTINGS } from "@zapier/utils";
import { useRouterParams } from "../../utils/useRouterParams";
interface Props {
  children: React.ReactNode;
}
interface DefaultViewContextType {
  handleResetDefaultView: () => void;
  handleSaveDefaultView: () => void;
  isButtonsDisabled: boolean;
  setDefaultView: () => void;
}
const defaultViewContext = {
  handleResetDefaultView: () => null,
  handleSaveDefaultView: () => null,
  isButtonsDisabled: false,
  setDefaultView: () => null
};
const DefaultViewContext = createContext<DefaultViewContextType>(defaultViewContext);
export function useDefaultView(): DefaultViewContextType {
  return useContext(DefaultViewContext);
}
export function DefaultViewProvider(props: Props) {
  const {
    emit
  } = useEmitSetDefaultViewEvent();
  const {
    createNotification
  } = useNotification();
  const pathname = usePathname();
  const currentAccountId = useCurrentAccountId();
  const {
    app,
    connection,
    folderType,
    fullUrl,
    OMITTED_FOLDER_QUERY_PARAMS,
    OMITTED_ZAP_QUERY_PARAMS,
    order,
    owner,
    search,
    setParams,
    shared,
    status,
    clearAllParams
  } = useRouterParams();
  const currentDefaultViewSelection = omitBy({
    app,
    connection,
    folderType,
    order,
    owner,
    pathname,
    search,
    shared,
    status
  }, isNull);
  const storedDefaultViewSettings = typeof window !== "undefined" ? localStorage?.getItem(`${DEFAULT_VIEW_SETTINGS}-${currentAccountId}`) : null;

  // Disable both buttons if current default view selection is the same as stored default view
  // OR if current default view selection is empty and user has no stored default view settings
  const isButtonsDisabled = isEqual(currentDefaultViewSelection, storedDefaultViewSettings ? JSON.parse(storedDefaultViewSettings) : {}) || !storedDefaultViewSettings && isEmpty(omit(currentDefaultViewSelection, ["pathname"]));
  const setDefaultView = useCallback(async () => {
    if (storedDefaultViewSettings) {
      const retrievedDefaultViewSettings = JSON.parse(storedDefaultViewSettings);
      setParams(retrievedDefaultViewSettings, ["pathname"], retrievedDefaultViewSettings.pathname, true);
    } else {
      // Clear all params if user has no stored default view settings - set to system default view settings (app/assets/zaps)
      clearAllParams();
    }
  }, [storedDefaultViewSettings]);

  // Read from localStorage if user has default view set
  useEffect(() => {
    // Only apply default view if user is on `app/assets/zaps` or `app/assets/folders`
    if (fullUrl === "/app/assets/zaps" || fullUrl === "/app/assets/zaps/folders") {
      setDefaultView();
    }
  }, []);
  const handleSaveDefaultView = useCallback(() => {
    if (!currentDefaultViewSelection?.pathname) {
      createNotification({
        message: "Default view cannot be saved",
        type: "error"
      });
      return;
    }
    if (currentDefaultViewSelection.pathname.includes("zaps/folders")) {
      // Remove specific zap filter params for default view on folders
      const folderDefaultViewSelection = omit(currentDefaultViewSelection, OMITTED_FOLDER_QUERY_PARAMS);
      localStorage.setItem(`${DEFAULT_VIEW_SETTINGS}-${currentAccountId}`, JSON.stringify(folderDefaultViewSelection));
    } else {
      // Remove specific folder filter params for default view on Zaps
      const zapDefaultViewSelection = omit(currentDefaultViewSelection, OMITTED_ZAP_QUERY_PARAMS);
      localStorage.setItem(`${DEFAULT_VIEW_SETTINGS}-${currentAccountId}`, JSON.stringify(zapDefaultViewSelection));
    }
    emit({
      optionSelected: SetDefaultViewOptions.Set,
      interactionRawText: fullUrl
    });
    createNotification({
      message: "Default view saved",
      type: "success"
    });
  }, [currentDefaultViewSelection, createNotification]);

  // Resets the view to the stored default view
  const handleResetDefaultView = useCallback(() => {
    setDefaultView();
  }, [storedDefaultViewSettings, setDefaultView, createNotification]);
  const value = {
    handleResetDefaultView,
    handleSaveDefaultView,
    isButtonsDisabled,
    setDefaultView
  };
  return <DefaultViewContext.Provider value={value} data-sentry-element="unknown" data-sentry-component="DefaultViewProvider" data-sentry-source-file="DefaultViewContext.tsx">
      {props.children}
    </DefaultViewContext.Provider>;
}